import * as Sentry from '@sentry/react';
import React from 'react';

type Props = { children: React.ReactNode };
type State = { hasError: boolean };

/**
 * Catches runtime errors (including ReferenceErrors from template
 * compilation/execution, e.g. "Can't find variable: output") that
 * bubble up from the portal Container or its children on the /clinic
 * route. Without this boundary the whole page goes blank; with it the
 * patient sees a recoverable message and can reload.
 */
export class PortalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
      tags: { boundary: 'portal' },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-dvh flex flex-col items-center justify-center px-4 antialiased bg-linear-to-br from-primary to-primary/60">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg p-8 text-center">
            <h1 className="text-lg font-semibold text-gray-900 mb-2">
              Something went wrong
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              We were unable to load the portal. Please reload the page — if
              the problem continues, contact your clinic directly.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="w-full rounded-xl px-4 py-2.5 text-sm font-semibold text-white bg-primary ring-1 ring-primary hover:bg-primary/90"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}